'use client';

import { useMemo } from 'react';

import { usePlanAccess } from 'src/hooks/use-plan-access';

import { IPlanService } from 'src/constants/plan-service';

import { countEnhancedCollectionToggles } from '../../utils/keyword-campaign-normalize';

/**
 * 플랜 사용량 — 「지금 몇 개를 쓰고 있고 몇 개까지 되는가」.
 *
 * 세는 기준은 **워크스페이스 문서의 파생 필드**(`keywords` · `accounts`)다.
 * 캠페인을 저장할 때 `recalcWorkspaceKeywordsFromCampaigns` 가 다시 계산해 넣는다.
 *
 */
export function useKeywordPlanUsageModel(effectiveWorkspace: any) {
  const { getLimit } = usePlanAccess();

  // 구독 플랜에서 허용된 키워드 총량(계정 포함)
  const keywordCount = getLimit(IPlanService.keyword.keywordCount);

  // 현재 워크스페이스가 사용 중인 키워드+계정 수
  const usedKeywordCount = useMemo(() => {
    const ws = effectiveWorkspace;
    const kwLen = Array.isArray(ws?.keywords) ? ws.keywords.length : 0;
    const accLen = Array.isArray(ws?.accounts) ? ws.accounts.length : 0;
    return kwLen + accLen;
  }, [effectiveWorkspace]);

  return { keywordCount, usedKeywordCount };
}

export type EnhancedCollectionUsageParams = {
  savedCampaigns: unknown;
  editingId: string | null;
  showNewCampaignForm: boolean;
  currentKeywordSets: unknown;
};

/**
 * 강화 수집 사용량 — 한도가 **워크스페이스 전체 합산**이라 각 폼에는
 * 「이 폼 밖에서 이미 쓰고 있는 수」를 넘겨야 한다.
 *
 * - 편집 중인 캠페인은 폼이 실시간으로 세므로 여기서 제외한다.
 * - 새 캠페인 폼이 열려 있으면 아직 저장 전인 세트들도 사용량에 포함한다.
 *
 */
export function useEnhancedCollectionUsageModel({
  savedCampaigns,
  editingId,
  showNewCampaignForm,
  currentKeywordSets,
}: EnhancedCollectionUsageParams) {
  const enhancedUsedByOtherCampaigns = useMemo(() => (Array.isArray(savedCampaigns) ? savedCampaigns : [])
    .filter((c: any) => String(c?.id || '') !== String(editingId || ''))
    .reduce((sum: number, c: any) => sum + countEnhancedCollectionToggles(c?.enhancedCollections), 0),
    [savedCampaigns, editingId]);

  const enhancedUsedByDraftSets = useMemo(() => (
    showNewCampaignForm
      ? (Array.isArray(currentKeywordSets) ? currentKeywordSets : [])
        .reduce((sum: number, s: any) => sum + countEnhancedCollectionToggles(s?.enhancedCollections), 0)
      : 0
  ), [showNewCampaignForm, currentKeywordSets]);

  return { enhancedUsedByOtherCampaigns, enhancedUsedByDraftSets };
}
